import { useRef } from "react";
import { useDispatch, useSelector } from "react-redux";

import AttachFileIcon from "@mui/icons-material/AttachFile";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import Drawer from "@mui/material/Drawer";
import Input from "@mui/material/Input";
import List from "@mui/material/List";

import UploadListItem from "./UploadListItem";
import {
  actionSheetUploadToggled,
  selectorSheets,
  actionSheetFileUploaded,
  actionSheetFileUpdated,
} from ".";
import {
  getNextSheetId,
  wbFromFile,
  defaultSheetFile,
  reduxSheetFile,
  jsonFromWb,
  preFormatJson,
} from "./spreadsheetutils";

const btnSecondary_ = (theme) => ({
  color: "black",
  backgroundColor: "#E0E0E0",
});

const drawer_ = (theme) => ({
  width: "100vw",
  minWidth: 335,
  maxWidth: 600,
  height: "100vh",
  [theme.breakpoints.up("md")]: {
    width: "80vw",
  },
  [theme.breakpoints.down("md")]: {
    width: "90vw",
  },
  [theme.breakpoints.down("sm")]: {
    width: "95vw",
  },
});

const topBar_ = (theme) => ({
  width: "100%",
  display: "flex",
  zIndex: 99,
  justifyContent: "space-between",
  alignItems: "center",
});

const grow_ = (theme) => ({
  flexGrow: 1,
});

const flexColumn_ = (theme) => ({
  display: "flex",
  flexDirection: "column",
  height: "100%",
});

const button_ = (theme) => ({
  margin: "10px",
});

export default function Upload() {
  const isVisible = useSelector((state) => state.spreadsheetReducer.uploadOpen);
  const dispatch = useDispatch();

  const inputRef = useRef(null);
  const files = useSelector(selectorSheets);
  const nextId = getNextSheetId(files);

  const toggleUpload = () => {
    dispatch(actionSheetUploadToggled());
  };

  const handleFile = (e) => {
    const files = [...e.target.files];
    files.forEach((file, index) => {
      const id = nextId + index;
      const fileName = file.name;
      // dispatch empty file before processing upload
      dispatch(
        actionSheetFileUploaded({
          ...defaultSheetFile(id),
          ...{
            id: id,
            fileName: fileName,
            json: [[], []],
            source: "local",
            status: "loading",
          },
        }),
      );
      (async (file) => {
        const workbook = await wbFromFile(file);
        const rawJson = jsonFromWb(workbook);
        const { headerRowIndex, rmrecnbr, lnglat, type, json, preHeaderJson } =
          preFormatJson(rawJson);

        dispatch(
          actionSheetFileUpdated({
            ...{
              id: id,
              fileName: fileName,
              rmrecnbr: rmrecnbr,
              json: json,
              headerRowIndex: headerRowIndex,
              preHeaderJson: preHeaderJson,
              lnglat: lnglat,
              defaultText: rmrecnbr,
              type: type,
              status: "complete",
            },
            ...reduxSheetFile(),
          }),
        );
      })(file);
    });

    // Now that all incoming files have been "kicked off", we can
    // close this panel. The user can address any missing col
    // headers in the spreadsheet component itself.
    toggleUpload();
  };

  return (
    <Drawer
      open={isVisible}
      onClose={toggleUpload}
      anchor="bottom"
      slotProps={{
        paper: {
          sx: drawer_,
        },
      }}
    >
      <Container style={{ height: "100%" }}>
        <Box sx={flexColumn_}>
          <Box sx={topBar_}>
            <Box>
              <Button
                variant="contained"
                color="primary"
                component="label"
                sx={button_}
                startIcon={<AttachFileIcon />}
              >
                Add Local File
                <Input
                  accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                  color="primary"
                  type="file"
                  onChange={handleFile}
                  inputRef={inputRef}
                  style={{ display: "none" }}
                  inputProps={{ multiple: true }}
                />
              </Button>
            </Box>
            <Box>
              <Button
                sx={btnSecondary_}
                variant="contained"
                onClick={toggleUpload}
              >
                Done
              </Button>
            </Box>
          </Box>
          <Box sx={grow_}>
            <List sx={grow_}>
              {files
                .filter((file) => file.source === "local")
                .map((file) => {
                  return <UploadListItem key={file.id} sheetId={file.id} />;
                })}
            </List>
          </Box>
        </Box>
      </Container>
    </Drawer>
  );
}
